import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner, Table, Badge } from "react-bootstrap";
import { RefreshCw, Download } from "react-feather";
import { useGet, useGetLazy, usePost } from "../../api";
import {
  IKindredNonAgenticRow,
  IKindredNonAgenticSnapshotListItem,
  IKindredNonAgenticSnapshotResponse,
} from "../../interfaces";
import { settingsType, useSettings } from "../../hooks";

function formatDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function exportToCsv(rows: IKindredNonAgenticRow[], filename: string) {
  const headers = [
    "Brand Name",
    "Code",
    "Summary",
    "Domains",
    "Inferred Domain",
    "Country Codes",
    "Source",
    "Valid From",
    "Valid To",
    "Is Agentic",
    "Brand ID",
    "Coupon ID",
  ];

  const csvRows = rows.map((r) => [
    r.brandName,
    r.code,
    r.summary ?? "",
    r.domains ?? "",
    r.inferredDomain ?? "",
    r.countryCodes ?? "",
    r.source,
    formatDate(r.validFrom),
    formatDate(r.validTo),
    r.isAgentic ? "yes" : "no",
    r.brandId,
    r.id,
  ]);

  const escape = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [
    headers.map(escape).join(","),
    ...csvRows.map((row) => row.map(escape).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const KindredNonAgentic: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>("");
  const [includeAgentic, setIncludeAgentic] = useState(false);

  const [brandName, setBrandName] = useSettings(
    settingsType.KINDRED_NON_AGENTIC_BRAND_NAME
  );
  const [countryCode, setCountryCode] = useSettings(
    settingsType.KINDRED_NON_AGENTIC_COUNTRY
  );
  const [domain, setDomain] = useSettings(
    settingsType.KINDRED_NON_AGENTIC_DOMAIN
  );
  const [source, setSource] = useSettings(
    settingsType.KINDRED_NON_AGENTIC_SOURCE
  );

  const createSnapshot = usePost<IKindredNonAgenticSnapshotListItem, void>();
  const snapshotGet = useGetLazy<IKindredNonAgenticSnapshotResponse>();
  const exportGet = useGetLazy<IKindredNonAgenticRow[]>();

  const snapshots = useGet<IKindredNonAgenticSnapshotListItem[]>(
    "kindred/non-agentic/snapshots",
    true
  );

  // Auto-select the latest snapshot when the list first loads
  useEffect(() => {
    if (snapshots.data?.length && !selectedSnapshotId) {
      setSelectedSnapshotId(snapshots.data[0].id);
    }
  }, [snapshots.data]);

  // Auto-select newly generated snapshot
  useEffect(() => {
    if (createSnapshot.data?.id) {
      setSelectedSnapshotId(createSnapshot.data.id);
    }
  }, [createSnapshot.data]);

  const buildFilterParams = useCallback(
    (p: number, l: number) => {
      let params = `page=${p}&limit=${l}`;
      if (brandName) params += `&brandName=${encodeURIComponent(brandName)}`;
      if (countryCode)
        params += `&countryCode=${encodeURIComponent(countryCode)}`;
      if (domain) params += `&domain=${encodeURIComponent(domain)}`;
      if (source) params += `&source=${encodeURIComponent(source)}`;
      if (includeAgentic) params += `&includeAgentic=true`;
      return params;
    },
    [brandName, countryCode, domain, source, includeAgentic]
  );

  const loadSnapshot = useCallback(
    (p: number) => {
      if (!selectedSnapshotId) return;
      snapshotGet.execute(
        `kindred/non-agentic/snapshots/${selectedSnapshotId}?${buildFilterParams(p, limit)}`
      );
    },
    [selectedSnapshotId, limit, buildFilterParams, snapshotGet]
  );

  useEffect(() => {
    if (selectedSnapshotId) loadSnapshot(page);
  }, [selectedSnapshotId, page]);

  const handleSearch = () => {
    setPage(1);
    loadSnapshot(1);
  };

  const handleClearFilters = () => {
    setBrandName("");
    setCountryCode("");
    setDomain("");
    setSource("");
    setIncludeAgentic(false);
  };

  const handleGenerateSnapshot = async () => {
    await createSnapshot.execute("kindred/non-agentic/snapshot");
    await snapshots.refetch();
  };

  // Export calls the dedicated /export endpoint — no pagination, all rows
  const handleExportCsv = () => {
    if (!selectedSnapshotId) return;
    let params = "";
    if (brandName) params += `&brandName=${encodeURIComponent(brandName)}`;
    if (countryCode)
      params += `&countryCode=${encodeURIComponent(countryCode)}`;
    if (domain) params += `&domain=${encodeURIComponent(domain)}`;
    if (source) params += `&source=${encodeURIComponent(source)}`;
    if (includeAgentic) params += `&includeAgentic=true`;
    exportGet.execute(
      `kindred/non-agentic/snapshots/${selectedSnapshotId}/export${
        params ? `?${params.slice(1)}` : ""
      }`
    );
  };

  useEffect(() => {
    if (exportGet.data && !exportGet.loading) {
      const date =
        snapshotGet.data?.snapshotDate.slice(0, 10) ??
        new Date().toISOString().slice(0, 10);
      exportToCsv(exportGet.data, `non_agentic_merchants_${date}.csv`);
    }
  }, [exportGet.data]);

  const pagination = snapshotGet.data?.pagination;
  const rows = snapshotGet.data?.results ?? [];

  const paginationControls = pagination && pagination.totalPages > 1 && (
    <div className="d-flex align-items-center gap-2">
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page <= 1}
        onClick={() => setPage((p) => p - 1)}
      >
        Previous
      </Button>
      <span className="small">
        Page {pagination.page} of {pagination.totalPages}
      </span>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page >= pagination.totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Next
      </Button>
    </div>
  );

  return (
    <div>
      {/* Title + Generate Snapshot */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Kindred Live — Non-Agentic Merchants</h4>
        <Button
          size="sm"
          variant="outline-success"
          onClick={handleGenerateSnapshot}
          disabled={createSnapshot.loading}
          title="Generate today's snapshot from SQL"
        >
          {createSnapshot.loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <>
              <RefreshCw size={14} className="me-1" /> Generate Snapshot
            </>
          )}
        </Button>
      </div>

      {createSnapshot.data && (
        <div className="alert alert-success py-1 small mb-3">
          Snapshot generated for{" "}
          {new Date(createSnapshot.data.snapshotDate).toLocaleDateString()} —{" "}
          {createSnapshot.data.total.toLocaleString()} total rows (
          {createSnapshot.data.totalNonAgentic.toLocaleString()} non-agentic)
          saved.
        </div>
      )}

      {/* Snapshot selector */}
      <Row className="mb-3 g-2 align-items-end">
        <Col md={5}>
          <Form.Label className="small">Snapshot</Form.Label>
          <Form.Select
            size="sm"
            value={selectedSnapshotId}
            onChange={(e) => {
              setSelectedSnapshotId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">— Select a snapshot —</option>
            {(snapshots.data || []).map((s) => (
              <option key={s.id} value={s.id}>
                {new Date(s.snapshotDate).toLocaleDateString()} (
                {s.total.toLocaleString()} total,{" "}
                {s.totalNonAgentic.toLocaleString()} non-agentic)
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {!selectedSnapshotId && !snapshots.loading && (
        <div className="alert alert-info py-2 small mb-3">
          No snapshot selected. Click <strong>Generate Snapshot</strong> to
          create one, or select an existing snapshot above.
        </div>
      )}

      {/* Filters */}
      <Row className="mb-2 g-2">
        <Col md={3}>
          <Form.Label className="small">Brand Name</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search brand name..."
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Col>
        <Col md={2}>
          <Form.Label className="small">Country Code</Form.Label>
          <Form.Control
            size="sm"
            placeholder="e.g. UK"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Domain</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search domain..."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Col>
        <Col md={2}>
          <Form.Label className="small">Source</Form.Label>
          <Form.Control
            size="sm"
            placeholder="e.g. awin"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </Col>
        <Col md={2} className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            id="includeAgentic"
            label="Include agentic"
            className="small mb-1"
            checked={includeAgentic}
            onChange={(e) => setIncludeAgentic(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSearch}
            disabled={!selectedSnapshotId}
          >
            {snapshotGet.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Search"
            )}
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-dark"
            onClick={handleExportCsv}
            disabled={
              !selectedSnapshotId || rows.length === 0 || exportGet.loading
            }
            title="Export all matching rows to CSV"
          >
            {exportGet.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <Download size={14} className="me-1" /> Export CSV
              </>
            )}
          </Button>
        </Col>
      </Row>

      {/* Status bar + pagination (top) */}
      {pagination && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted small">
            Showing {rows.length.toLocaleString()} of{" "}
            {pagination.total.toLocaleString()} results
          </span>
          {paginationControls}
        </div>
      )}

      {snapshotGet.error && (
        <div className="text-danger mb-3">
          Error: {snapshotGet.error.message}
        </div>
      )}

      {/* Table */}
      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>Brand Name</th>
            <th>Code</th>
            <th>Summary</th>
            <th>Domains</th>
            <th>Inferred Domain</th>
            <th>Countries</th>
            <th>Source</th>
            <th>Valid From</th>
            <th>Valid To</th>
            <th>Agentic</th>
          </tr>
        </thead>
        <tbody>
          {snapshotGet.loading && (
            <tr>
              <td colSpan={10} className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading...
              </td>
            </tr>
          )}
          {!snapshotGet.loading && !selectedSnapshotId && (
            <tr>
              <td colSpan={10} className="text-center text-muted py-3">
                Select a snapshot above to view data.
              </td>
            </tr>
          )}
          {!snapshotGet.loading && selectedSnapshotId && rows.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted py-3">
                No results match your filters.
              </td>
            </tr>
          )}
          {rows.map((row, idx) => (
            <tr key={`${row.brandId}-${row.code}-${idx}`}>
              <td>{row.brandName}</td>
              <td>
                <code>{row.code}</code>
              </td>
              <td className="text-truncate" style={{ maxWidth: 200 }}>
                {row.summary || "—"}
              </td>
              <td className="small">{row.domains || "—"}</td>
              <td className="small">{row.inferredDomain || "—"}</td>
              <td>
                {row.countryCodes
                  ? row.countryCodes.split(",").map((cc) => (
                      <Badge key={cc} bg="secondary" className="me-1">
                        {cc.trim()}
                      </Badge>
                    ))
                  : "—"}
              </td>
              <td>{row.source}</td>
              <td>{formatDate(row.validFrom)}</td>
              <td>{formatDate(row.validTo)}</td>
              <td>
                {row.isAgentic ? (
                  <Badge bg="warning" text="dark">
                    agentic
                  </Badge>
                ) : (
                  <Badge bg="success">no</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Pagination (bottom) */}
      {paginationControls && (
        <div className="d-flex justify-content-center mt-2">
          {paginationControls}
        </div>
      )}
    </div>
  );
};
